import { Injectable, InjectionToken } from '@angular/core';
import { Observable, BehaviorSubject, of, combineLatest, throwError, timer } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, tap, throttleTime, finalize, shareReplay, switchMap, filter } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import {
  DashboardDTO,
  DashboardConfigDTO,
  DashboardSelectorDTO,
  DashboardWidgetConfigDTO,
  DashboardFilterDTO,
  UserProgressWidgetDataDTO,
  UserProgressDTO,
  DashboardRequestDTO
} from '../../dto/dashboard/dashboard.dto';
import { DashboardDataRequestDTO } from '../../dto/dashboard/dashboard-data-request.dto';
import { ListResponseDTO } from '../../dto/list-response.dto';
import { Dashboard, DashboardWidget, DashboardWidgetSettings } from '../../models/dashboard/Dashboard.model';
import { DashboardTile, DashboardTileModel } from '../../models/dashboard/DashboardTile.model';
import { getUserProgressData, getEducationalPaths, getUserStatusData } from './data';
import { buildDashboardDataPostUrl } from './dashboard-share-link-api';
import { ListRequestDTO, createListRequest } from '../../dto/list-request.dto';

// Create token that can be used to provide the service
export const DASHBOARD_SERVICE_TOKEN = new InjectionToken<DashboardService>('DashboardService');

// Export the DashboardDataSource interface
export interface DashboardDataSource<T> {
  id: string;
  name: string;
  description: string;
  dataSource: () => Observable<T[]>;
  visualizations: {
    id: string;
    name: string;
    description: string;
    type: string;
    dataSource: (data: T[]) => any;
    settings: any;
    requiredRoles?: string[];
  }[];
}

// Storage key constants
const DASHBOARD_STORAGE_KEY = 'dashboard_tiles';
const DUNA_DASHBOARD_STORAGE_KEY = 'duna_dashboard';
const DASHBOARD_CACHE_STORAGE_KEY = 'dashboard-cache-v1';
const DEFAULT_DASHBOARD_ID = 'default';

// Cache interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Main dashboard service that manages all dashboard-related operations.
 * Simplified implementation with focus on current needs while maintaining
 * backward compatibility.
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  // Add a static flag to track if service has been initialized
  private static hasInitialized = false;
  private initializing = false;
  private apiTestInProgress = false;  // Add flag to track API test status

  // Initialize with default URL and update in constructor
  private apiUrl: string = `${environment.apiUrl}/dashboard`;
  
  // Cache with TTL
  private widgetDataCache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes default TTL

  // Flag to track if the API is available
  private apiAvailable = true; 
  // Track last save time to throttle
  private lastSaveTime = 0;
  // Minimum time between save operations (3 seconds)
  private readonly SAVE_THROTTLE_MS = 3000;

  // Legacy dashboard data - will be deprecated in future versions
  private dashboardTilesSubject = new BehaviorSubject<DashboardTileModel[]>([]);
  dashboardTiles$ = this.dashboardTilesSubject.asObservable();
  
  // Current Dashboard model (deprecated approach)
  private dashboardSubject = new BehaviorSubject<Dashboard | null>(null);
  dashboard$ = this.dashboardSubject.asObservable();
  
  // New Dashboard Config DTO approach
  private currentDashboard$ = new BehaviorSubject<DashboardConfigDTO | null>(null);
  private availableDashboards$ = new BehaviorSubject<DashboardSelectorDTO | null>(null);

  // Add private tracking variables to the class
  private lastResetTime = 0;
  private resetInProgress = false;
  private readonly RESET_THROTTLE_MS = 3000; // 3 second throttle

  constructor(
    private http: HttpClient
  ) {
    // Prevent multiple initializations
    if (DashboardService.hasInitialized || this.initializing) {
      return;
    }
    
    // Set initialization flags
    this.initializing = true;
    DashboardService.hasInitialized = true;
    
    
    // Initialize all dashboard systems for backward compatibility
    this.initializeServices();
    
    // Mark initialization as complete
    this.initializing = false;
  }
  
  /**
   * Separate initialization method to better control the sequence
   */
  private initializeServices(): void {
    
    // First load the cache
    const cachedDashboard = this.loadFromCache();
    if (cachedDashboard) {
      this.currentDashboard$.next(cachedDashboard);
    } else {
      // Don't create a hardcoded default - let the API call handle template loading
      this.currentDashboard$.next(null);
    }
    
    // Schedule cache cleanup
    this.scheduleCacheCleanup();
  }

  // ==========================================================================
  // PUBLIC API - CURRENT DASHBOARD OPERATIONS
  // ==========================================================================

  /**
   * Get the current dashboard configuration
   */
  getCurrentDashboardConfig(): Observable<DashboardConfigDTO | null> {
    // Don't automatically try to load a dashboard - this was causing automatic API calls
    // Just return the current value, let the component handle loading logic
    return this.currentDashboard$.asObservable();
  }

  /** Synchronous read of cached/current dashboard config (localStorage or last fetch). */
  peekCurrentDashboardConfig(): DashboardConfigDTO | null {
    return this.currentDashboard$.value;
  }

  /**
   * Get the current dashboard (could be either Dashboard model or ConfigDTO)
   * Provides backward compatibility with both implementations
   */
  getCurrentDashboard(): Observable<Dashboard | DashboardConfigDTO | null> {
    return combineLatest([
      this.dashboard$,
      this.currentDashboard$
    ]).pipe(
      map(([dashboard, configDto]) => {
        // Prefer the new DTO format if available
        return configDto || dashboard;
      })
    );
  }

  /**
   * Get available dashboards for selection
   */
  getAvailableDashboards(): Observable<DashboardSelectorDTO | null> {
    this.loadAvailableDashboards();
    return this.availableDashboards$.asObservable();
  }

  /**
   * Set the current active dashboard
   */
  setCurrentDashboard(id: string): Observable<DashboardConfigDTO | null> {
    return this.http.post<DashboardConfigDTO>(`${this.apiUrl}/set-current/${id}`, {}).pipe(
      tap(dashboard => this.currentDashboard$.next(dashboard)),
      catchError(error => {
        console.error('Error setting current dashboard:', error);
        return of(null);
      })
    );
  }

  /**
   * Reset to the system default dashboard
   * @param manualReset Set to true to indicate this was triggered manually by user action (required)
   */
  resetToDefault(manualReset: boolean = false): Observable<DashboardConfigDTO | null> {
    
    // Prevent automatic resets - require explicit manual confirmation
    if (!manualReset) {
      console.warn('[DASHBOARD] Attempted automatic reset-to-default was prevented - manual reset required');
      // Return current dashboard or null if none exists
      const currentDashboard = this.currentDashboard$.value;
      return of(currentDashboard || null);
    }
    
    // Prevent multiple resets within a short time period
    const now = Date.now();
    if (this.resetInProgress) {
      console.warn('[DASHBOARD] Reset already in progress, skipping duplicate call');
      const currentDashboard = this.currentDashboard$.value;
      return of(currentDashboard || null);
    }
    
    if (now - this.lastResetTime < this.RESET_THROTTLE_MS) {
      console.warn(`[DASHBOARD] Reset throttled - called again within ${this.RESET_THROTTLE_MS}ms`);
      const currentDashboard = this.currentDashboard$.value;
      return of(currentDashboard || null);
    }
    
    // Update tracking variables
    this.lastResetTime = now;
    this.resetInProgress = true;
    
    return this.http.post<DashboardConfigDTO>(`${this.apiUrl}/reset-to-default`, {}).pipe(
      tap(defaultDashboard => {
        this.currentDashboard$.next(defaultDashboard);
        this.loadAvailableDashboards();
        this.resetInProgress = false;
      }),
      catchError(error => {
        console.error('[DASHBOARD] Error resetting to default dashboard:', error);
        this.resetInProgress = false;
        this.currentDashboard$.next(null);
        return of(null);
      })
    );
  }

  /**
   * Save the current dashboard configuration
   * This will update the current dashboard config and save it to local storage
   * @param dashboard The dashboard configuration to save
   * @param saveToApi Whether to save to the API (defaults to false)
   */
  saveCurrentDashboardConfig(dashboard: DashboardConfigDTO, saveToApi: boolean = false): Observable<DashboardConfigDTO> {
    
    // Log widget sizes before saving
    if (dashboard?.widgets) {
      dashboard.widgets.forEach(widget => {
      });
    }
    
    // Update the current dashboard
    this.currentDashboard$.next(dashboard);
    
    // Save to cache
    this.saveToCache(dashboard);
    
    // Check if we should make an API call
    if (!saveToApi) {
      return of(dashboard);
    }
    
    // Check if we should throttle API calls
    const now = Date.now();
    const timeSinceLastSave = now - this.lastSaveTime;
    
    // Skip API call if API is known to be unavailable or if we're throttling
    if (!this.apiAvailable || timeSinceLastSave < this.SAVE_THROTTLE_MS) {
      return of(dashboard);
    }
    
    // Update timestamp
    this.lastSaveTime = now;
    
    // Save to API if possible
    return this.http.post<DashboardConfigDTO>(
      `${this.apiUrl}/save`,
      dashboard
    ).pipe(
      tap(savedDashboard => {
        
        // Log saved widget sizes
        if (savedDashboard?.widgets) {
          savedDashboard.widgets.forEach(widget => {
          });
        }
        
        // Update the current dashboard with the server response
        this.currentDashboard$.next(savedDashboard);
        
        // Save to cache
        this.saveToCache(savedDashboard);
      }),
      catchError((error: HttpErrorResponse) => {
        console.warn('[DASHBOARD] Could not save dashboard to API:', error.status, error.message);
        
        // Handle API errors
        this.handleApiError(error, 'save dashboard');
        
        // Still return the dashboard we tried to save
        return of(dashboard);
      })
    );
  }

  /**
   * Directly fetch the current dashboard from the /current endpoint
   * This is used to avoid the automatic loading logic in getCurrentDashboardConfig
   * Retries on failure to ensure widgets are loaded from backend
   */
  fetchCurrentDashboardDirectly(retryCount: number = 0, maxRetries: number = 3): Observable<DashboardConfigDTO | null> {
    return this.requestCurrentDashboardConfig().pipe(
      tap(response => {
        if (response) {
          this.currentDashboard$.next(response);
          this.saveToCache(response);
        } else {
          console.warn('[DASHBOARD] Received null/empty response from /current endpoint');
        }
      }),
      catchError((error: unknown) => {
        this.logDashboardCurrentFetchError(error, retryCount + 1);

        if (error instanceof HttpErrorResponse) {
          this.handleApiError(error, 'load current dashboard');
        }

        const shouldRetry = retryCount < maxRetries && !this.isNonRetryableDashboardFetchError(error);
        if (shouldRetry) {
          return timer(1000).pipe(
            switchMap(() => this.fetchCurrentDashboardDirectly(retryCount + 1, maxRetries))
          );
        }

        console.warn('[DASHBOARD] Max retries reached, checking cache as last resort');
        const cachedDashboard = this.loadFromCache();
        if (cachedDashboard && cachedDashboard.widgets && cachedDashboard.widgets.length > 0) {
          this.currentDashboard$.next(cachedDashboard);
          return of(cachedDashboard);
        }

        console.error('[DASHBOARD] Failed to load dashboard from API after retries and no valid cache found');
        this.currentDashboard$.next(null);
        return of(null);
      })
    );
  }

  private requestCurrentDashboardConfig(): Observable<DashboardConfigDTO | null> {
    return this.http.get(`${this.apiUrl}/current`, { responseType: 'text' }).pipe(
      map((rawBody) => this.parseCurrentDashboardResponse(rawBody))
    );
  }

  private parseCurrentDashboardResponse(rawBody: string): DashboardConfigDTO | null {
    const trimmed = (rawBody ?? '').trim();
    if (!trimmed) {
      throw new Error('[DASHBOARD] Empty response body from /dashboard/current');
    }

    const candidates = this.buildDashboardJsonParseCandidates(trimmed);
    let lastParseError: unknown;

    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate) as DashboardConfigDTO;
        return this.normalizeDashboardConfig(parsed);
      } catch (error) {
        lastParseError = error;
      }
    }

    console.error('[DASHBOARD] Failed to parse /dashboard/current response as JSON', {
      parseError: lastParseError,
      bodyLength: trimmed.length,
      bodyPreview: trimmed.slice(0, 500),
      bodyTail: trimmed.length > 500 ? trimmed.slice(-200) : undefined,
      candidateCount: candidates.length,
    });

    throw lastParseError instanceof Error
      ? lastParseError
      : new Error('[DASHBOARD] Invalid JSON in /dashboard/current response');
  }

  private buildDashboardJsonParseCandidates(trimmed: string): string[] {
    const candidates = new Set<string>([trimmed]);

    const firstObjectStart = trimmed.indexOf('{');
    const lastObjectEnd = trimmed.lastIndexOf('}');
    if (firstObjectStart !== -1 && lastObjectEnd > firstObjectStart) {
      candidates.add(trimmed.slice(firstObjectStart, lastObjectEnd + 1));
    }

    const trailingJsonMatch = trimmed.match(/\{[\s\S]*\}\s*$/);
    if (trailingJsonMatch?.[0]) {
      candidates.add(trailingJsonMatch[0].trim());
    }

    return Array.from(candidates);
  }

  private logDashboardCurrentFetchError(error: unknown, attempt: number): void {
    if (error instanceof HttpErrorResponse) {
      const rawBody = typeof error.error === 'string'
        ? error.error
        : error.error instanceof ProgressEvent
          ? undefined
          : error.error;

      console.warn(
        `[DASHBOARD] Could not load current dashboard from API (attempt ${attempt}):`,
        error.status,
        error.message,
        rawBody ? { rawBodyPreview: String(rawBody).slice(0, 500) } : undefined
      );
      return;
    }

    console.warn(
      `[DASHBOARD] Could not load current dashboard from API (attempt ${attempt}):`,
      error
    );
  }

  private isNonRetryableDashboardFetchError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 200 || error.status === 500;
    }

    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      return message.includes('parse')
        || message.includes('json')
        || message.includes('empty response body')
        || message.includes('unexpected token');
    }

    return false;
  }

  // ==========================================================================
  // DASHBOARD CRUD OPERATIONS
  // ==========================================================================

  /**
   * Create a new dashboard
   */
  createDashboard(dashboard: DashboardConfigDTO): Observable<DashboardConfigDTO> {
    return this.http.post<DashboardConfigDTO>(`${this.apiUrl}`, dashboard).pipe(
      tap(newDashboard => {
        // Update available dashboards list
        this.loadAvailableDashboards();
        
        // If this is the default dashboard, also set it as current
        if (newDashboard.isDefault) {
          this.currentDashboard$.next(newDashboard);
        }
      }),
      catchError(error => {
        console.error('Error creating dashboard:', error);
        return throwError(() => new Error('Failed to create dashboard'));
      })
    );
  }

  /**
   * Update a dashboard
   */
  updateDashboard(id: string, dashboard: Partial<DashboardConfigDTO>): Observable<DashboardConfigDTO> {
    return this.http.put<DashboardConfigDTO>(`${this.apiUrl}/${id}`, dashboard).pipe(
      tap(updatedDashboard => {
        // If this is the current dashboard, update the current value
        const current = this.currentDashboard$.value;
        if (current && current.id === updatedDashboard.id) {
          this.currentDashboard$.next(updatedDashboard);
        }
        
        // Update available dashboards
        this.loadAvailableDashboards();
      }),
      catchError(error => {
        console.error('Error updating dashboard:', error);
        return throwError(() => new Error('Failed to update dashboard'));
      })
    );
  }

  /**
   * Delete a dashboard
   */
  deleteDashboard(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        // Update available dashboards
        this.loadAvailableDashboards();
        
        // If this was the current dashboard, set to null instead of loading a new one
        const current = this.currentDashboard$.value;
        if (current && current.id === id) {
          this.currentDashboard$.next(null);
        }
      }),
      catchError(error => {
        console.error('Error deleting dashboard:', error);
        return throwError(() => new Error('Failed to delete dashboard'));
      })
    );
  }

  /**
   * Set a dashboard as the user's default
   */
  setDefaultDashboard(id: string): Observable<any> {
    if (!this.apiAvailable) {
      console.warn('[DashboardService] API not available, cannot set default dashboard');
      return throwError(() => new Error('API not available'));
    }
    
    return this.http.post<any>(`${this.apiUrl}/${id}/set-default`, {})
      .pipe(
        tap(() => {
          // Refresh available dashboards to update the default flags
          this.loadAvailableDashboards();
        }),
        catchError(error => {
          this.handleApiError(error, 'setting default dashboard');
          return throwError(() => error);
        })
      );
  }

  // ==========================================================================
  // DASHBOARD TEMPLATES MANAGEMENT
  // ==========================================================================

  /**
   * Get all available dashboard templates
   * @returns Observable of dashboard templates
   */
  getDashboardTemplates(): Observable<any[]> {
    this.apiAvailable = true;

    return this.http.get<any[]>(`${this.apiUrl}/templates`)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'retrieving dashboard templates');
          return of([]);
        })
      );
  }

  /**
   * Save current dashboard as a template
   * @param dashboardId ID of the dashboard to save as template
   * @param name Template name
   * @param description Template description
   * @param isPublic Whether the template should be available to all organizations
   * @returns Observable of the created template
   */
  saveDashboardAsTemplate(
    dashboardId: string,
    name: string,
    description?: string,
    isPublic: boolean = false,
    targetRole?: string | null,
    widgets?: unknown[],
    targetRoles?: string[]
  ): Observable<any> {
    this.apiAvailable = true;

    const templateData = {
      dashboardId,
      name,
      description,
      isPublic,
      targetRole,
      targetRoles: targetRoles?.length ? targetRoles : undefined,
      widgets: widgets ?? undefined,
      templateData: widgets ? { widgets } : undefined,
    };
    
    return this.http.post<any>(`${this.apiUrl}/templates`, templateData)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'saving dashboard as template');
          return throwError(() => error);
        })
      );
  }

  /**
   * Update an existing dashboard template
   * @param templateId ID of the template to update
   * @param updates Template properties to update
   * @returns Observable of the updated template
   */
  updateDashboardTemplate(templateId: string, updates: {
    name?: string;
    description?: string;
    isPublic?: boolean;
    targetRole?: string | null;
    targetRoles?: string[];
    widgets?: unknown[];
    templateData?: { widgets: unknown[] };
  }): Observable<any> {
    this.apiAvailable = true;

    return this.http.put<any>(`${this.apiUrl}/templates/${templateId}`, updates)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'updating dashboard template');
          return throwError(() => error);
        })
      );
  }

  /**
   * Delete a dashboard template
   * @param templateId ID of the template to delete
   * @returns Observable of the delete response
   */
  deleteDashboardTemplate(templateId: string): Observable<any> {
    this.apiAvailable = true;

    return this.http.delete<any>(`${this.apiUrl}/templates/${templateId}`)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'deleting dashboard template');
          return throwError(() => error);
        })
      );
  }

  saveUserDashboardCustomization(dashboardId: string, payload: {
    name?: string;
    widgets: unknown[];
    templateData?: { widgets: unknown[] };
  }): Observable<DashboardConfigDTO> {
    this.apiAvailable = true;

    return this.http.put<DashboardConfigDTO>(`${this.apiUrl}/${dashboardId}`, {
      ...payload,
      userCustomizationOnly: true,
    }).pipe(
      map((response) => this.normalizeDashboardConfig(response) as DashboardConfigDTO),
      tap((dashboard) => {
        if (dashboard) {
          this.cacheCurrentDashboard(dashboard);
        }
      }),
      catchError(error => {
        this.handleApiError(error, 'saving user dashboard customization');
        return throwError(() => error);
      })
    );
  }

  saveLoadedDashboardLayout(dashboardId: string, payload: {
    name?: string;
    widgets: unknown[];
    templateData?: { widgets: unknown[] };
  }): Observable<DashboardConfigDTO> {
    this.apiAvailable = true;

    return this.http.put<DashboardConfigDTO>(`${this.apiUrl}/${dashboardId}`, payload).pipe(
      map((response) => this.normalizeDashboardConfig(response) as DashboardConfigDTO),
      tap((dashboard) => {
        if (dashboard) {
          this.cacheCurrentDashboard(dashboard);
        }
      }),
      catchError(error => {
        this.handleApiError(error, 'saving dashboard layout');
        return throwError(() => error);
      })
    );
  }

  cacheCurrentDashboard(dashboard: DashboardConfigDTO): void {
    this.currentDashboard$.next(dashboard);
    this.saveToCache(dashboard);
  }

  /**
   * Apply a dashboard template to create a new dashboard for the current user
   * @param templateId ID of the template to apply
   * @returns Observable of the created dashboard configuration
   */
  applyDashboardTemplate(templateId: string): Observable<DashboardConfigDTO> {
    this.apiAvailable = true;
    this.clearAllDashboardCache();

    return this.http.post<DashboardConfigDTO>(`${this.apiUrl}/apply-template/${templateId}`, {}).pipe(
      map((dashboard) => this.normalizeDashboardConfig(dashboard)),
      filter((dashboard): dashboard is DashboardConfigDTO => !!dashboard && Array.isArray(dashboard.widgets)),
      tap(dashboard => {
        this.apiAvailable = true;
        this.cacheCurrentDashboard(dashboard);
      }),
      catchError(error => {
        this.handleApiError(error, 'applying dashboard template');
        return throwError(() => error);
      })
    );
  }

  // ==========================================================================
  // WIDGET DATA OPERATIONS
  // ==========================================================================

  // Check if API is available (should be checked in catchError blocks)
  private handleApiError(error: HttpErrorResponse, context: string): void {
    const contextLower = context.toLowerCase();
    const shouldDisableApi =
      contextLower.includes('load current dashboard') ||
      contextLower.includes('widget data') ||
      contextLower.includes('load dashboard');

    if (error.status === 404 && shouldDisableApi) {
      const prevState = this.apiAvailable;
      this.apiAvailable = false;

      if (prevState !== this.apiAvailable) {
      }
    }

    if (error.status === 200 && error.message.includes('Http failure during parsing')) {
      console.error('[DASHBOARD] API returned HTTP 200 but body was not valid JSON', {
        context,
        url: error.url,
        message: error.message,
      });
    }
  }

  /**
   * Get data for a specific widget with filtering
   * @param widgetType The type of widget
   * @param dataSource The data source to use
   * @param filters Optional filters to apply
   */
  getWidgetData<T>(
    widgetType: string, 
    dataSource: string, 
    filters?: DashboardFilterDTO
  ): Observable<T> {
    // Create a cache key based on the parameters
    const cacheKey = `${widgetType}:${dataSource}:${JSON.stringify(filters || {})}`;
    
    
    // Check cache first
    const cached = this.getCachedData<T>(cacheKey);
    if (cached) {
      return of(cached);
    }
    
    // Special handling for userProgress type - delegate directly to the data source
    if (widgetType === 'userProgress') {
      const listRequest = createListRequest({
        page: 1,
        pageSize: 50,
        filters: filters,
        includeFilters: true
      });
      return getUserProgressData(this.http, listRequest) as unknown as Observable<T>;
    }
    
    // For other widget types, construct an endpoint
    const endpoint = `${this.apiUrl}/widget-data/${widgetType}`;
    
    // Log the request details
    
    // Make the API request
    return this.http.post<T>(endpoint, { dataSource, filters }).pipe(
      tap(data => {
        this.cacheData(cacheKey, data);
      }),
      catchError(error => {
        console.error(`[DASHBOARD] Error getting data for ${widgetType}:`, error);
        
        // For userProgress widgets, use the dedicated mock data generator
        if (widgetType === 'userProgress') {
          return this.generateMockUserProgressData(filters) as unknown as Observable<T>;
        }
        
        return of({} as T);
      })
    );
  }

  /**
   * Makes a standardized dashboard data request
   * @param sourceType Type of data source to request
   * @param request Dashboard data request parameters
   * @returns Observable of ListResponseDTO
   */
  public fetchDashboardData<T = any>(sourceType: string, request: Partial<DashboardDataRequestDTO>): Observable<ListResponseDTO<T>> {
    const fullRequest: DashboardDataRequestDTO = {
      ...request,
      sourceType,
      page: request.page || 1,
      pageSize: request.pageSize || 10
    };
    
    return this.http.post<ListResponseDTO<T>>(buildDashboardDataPostUrl(sourceType), fullRequest).pipe(
      tap(response => {
        // Cache the response
        const cacheKey = `${sourceType}:${JSON.stringify(request)}`;
        this.cacheData(cacheKey, response);
      }),
      catchError(error => {
        console.error(`[DASHBOARD] Error fetching data for ${sourceType}:`, error);
        // Return mock data for userProgress, otherwise empty response
        if (sourceType === 'userProgress') {
          const mockData = this.generateMockUserProgressData(request.filters);
          return of({
            data: mockData.users,
            meta: {
              page: request.page || 1,
              pageSize: request.pageSize || 10,
              totalItems: mockData.total,
              totalPages: Math.ceil(mockData.total / (request.pageSize || 10))
            }
          } as ListResponseDTO<T>);
        }
        return of({
          data: [],
          meta: {
            page: 1,
            pageSize: 10,
            totalItems: 0,
            totalPages: 0
          }
        } as ListResponseDTO<T>);
      })
    );
  }

  /**
   * Get user progress data using the new standardized API
   * @param filters - Optional filters for the data
   */
  getUserProgressData(filters?: DashboardFilterDTO): Observable<UserProgressWidgetDataDTO> {
    
    const request: DashboardDataRequestDTO = {
      sourceType: 'userProgress',
      page: 1,
      pageSize: 100,
      filters: filters || { group: 'all' },
      includeFilters: true
    };

    return this.fetchDashboardData<UserProgressDTO>(request.sourceType, request).pipe(
      map(response => ({
        users: response.data,
        total: response.meta.totalItems,
        filteredPath: filters?.['educationalPathId'] || '',
        filters: response.filters
      }))
    );
  }

  /**
   * Get user status data for the status widget
   * @param filters - Optional filters for the data
   */
  getUserStatusData(filters?: DashboardFilterDTO): Observable<any> {
    
    // Use the getUserStatusData function from status-widget.data.ts
    return getUserStatusData(this.http, filters).pipe(
      catchError(error => {
        console.error('[DASHBOARD] Error getting user status data:', error);
        
        // Return fallback data if API call fails
        return of({
          completedUsers: 0,
          totalUsers: 0,
          percentComplete: 0,
          inProgressUsers: 0,
          notStartedUsers: 0,
          statusDistribution: [
            { status: 'completed', count: 0 },
            { status: 'in-progress', count: 0 },
            { status: 'not-started', count: 0 }
          ]
        });
      })
    );
  }

  /**
   * Get available educational paths for user progress filtering
   */
  getEducationalPaths(): Observable<{ id: string; name: string; description?: string }[]> {
    return getEducationalPaths(this.http);
  }

  // ==========================================================================
  // WIDGET CRUD OPERATIONS
  // ==========================================================================

  /**
   * Add a widget to the current dashboard
   */
  addWidget(widget: DashboardWidgetConfigDTO): Observable<DashboardConfigDTO> {
    const current = this.currentDashboard$.value;
    if (!current) {
      return throwError(() => new Error('No current dashboard to add widget to'));
    }
    
    const updatedDashboard: DashboardConfigDTO = {
      ...current,
      widgets: [...current.widgets, widget],
      updatedAt: new Date()
    };
    
    return this.updateDashboard(current.id, updatedDashboard);
  }

  /**
   * Update a widget in the current dashboard
   */
  updateWidget(widgetId: string, updates: Partial<DashboardWidgetConfigDTO>): Observable<DashboardConfigDTO> {
    const current = this.currentDashboard$.value;
    if (!current) {
      return throwError(() => new Error('No current dashboard to update widget in'));
    }
    
    const widgetIndex = current.widgets.findIndex(w => w.id === widgetId);
    if (widgetIndex === -1) {
      return throwError(() => new Error(`Widget with ID ${widgetId} not found`));
    }
    
    const updatedWidgets = [...current.widgets];
    updatedWidgets[widgetIndex] = {
      ...updatedWidgets[widgetIndex],
      ...updates
    };
    
    const updatedDashboard: DashboardConfigDTO = {
      ...current,
      widgets: updatedWidgets,
      updatedAt: new Date()
    };
    
    return this.updateDashboard(current.id, updatedDashboard);
  }

  /**
   * Remove a widget from the current dashboard
   */
  removeWidget(widgetId: string): Observable<DashboardConfigDTO> {
    const current = this.currentDashboard$.value;
    if (!current) {
      return throwError(() => new Error('No current dashboard to remove widget from'));
    }
    
    const updatedWidgets = current.widgets.filter(w => w.id !== widgetId);
    
    const updatedDashboard: DashboardConfigDTO = {
      ...current,
      widgets: updatedWidgets,
      updatedAt: new Date()
    };
    
    return this.updateDashboard(current.id, updatedDashboard);
  }

  // ==========================================================================
  // CACHE MANAGEMENT
  // ==========================================================================

  /**
   * Clear the widget data cache
   * @param widgetType Optional widget type to clear cache for
   */
  clearWidgetDataCache(widgetType?: string): void {
    if (widgetType) {
      // Only clear cache for the specified widget type
      for (const key of this.widgetDataCache.keys()) {
        if (key.startsWith(`${widgetType}:`)) {
          this.widgetDataCache.delete(key);
        }
      }
    } else {
      // Clear entire cache
      this.widgetDataCache.clear();
    }
    
  }

  /**
   * Clear all dashboard-related localStorage and caches
   * Use this when you need to force a complete refresh
   */
  clearAllDashboardCache(): void {
    
    // Clear localStorage
    localStorage.removeItem(DASHBOARD_STORAGE_KEY);
    localStorage.removeItem(DUNA_DASHBOARD_STORAGE_KEY);
    localStorage.removeItem(DASHBOARD_CACHE_STORAGE_KEY);
    localStorage.removeItem('dunaWidgets');
    
    // Clear widget data cache
    this.clearWidgetDataCache();
    
    // Reset API availability to force fresh API calls
    this.apiAvailable = true;
    
    // Clear observables
    this.currentDashboard$.next(null);
    this.availableDashboards$.next(null);
    this.dashboardTilesSubject.next([]);
    this.dashboardSubject.next(null);
    
  }

  // ==========================================================================
  // PRIVATE IMPLEMENTATION METHODS
  // ==========================================================================

  /**
   * Fallback data for when API calls fail
   */
  private getFallbackData<T>(widgetType: string, filters?: DashboardFilterDTO): Observable<T> {
    // Simple mock data provider for different widget types
    
    let result: any;
    switch (widgetType) {
      case 'userProgress':
        // Ensure filters have at least a default group if empty
        const safeFilters = filters || { group: 'all' };
        if (Object.keys(safeFilters).length === 0) {
          safeFilters['group'] = 'all';
        }
        
        result = this.generateMockUserProgressData(safeFilters);
        break;
      case 'status':
        result = {
          percentComplete: 75,
          totalUsers: 120,
          completedUsers: 90
        };
        break;
      default:
        result = {};
        break;
    }
    
    return of(result as unknown as T);
  }

  /**
   * Mock data for user progress when API fails
   * @deprecated Use generateMockUserProgressData instead
   */
  private getMockUserProgressData(filters?: DashboardFilterDTO): UserProgressWidgetDataDTO {
    return this.generateMockUserProgressData(filters);
  }

  /**
   * Generate mock user progress data when API fails
   * @param filters - Filters to apply to the mock data
   */
  private generateMockUserProgressData(filters?: any): UserProgressWidgetDataDTO {
    
    // Base mock data - make sure we have a decent amount of test data
    let users: UserProgressDTO[] = [
      { id: '1', name: 'Anders Hansen', station: 'Station Nord', path: 'Indsatsleder', pathId: 'indsatsleder', percentComplete: 85 },
      { id: '2', name: 'Mette Jensen', station: 'Station Syd', path: 'Indsatsleder', pathId: 'indsatsleder', percentComplete: 72 },
      { id: '3', name: 'Lars Petersen', station: 'Station Øst', path: 'Holdleder', pathId: 'holdleder', percentComplete: 63 },
      { id: '4', name: 'Sofie Nielsen', station: 'Station Vest', path: 'Holdleder', pathId: 'holdleder', percentComplete: 91 },
      { id: '5', name: 'Jacob Andersen', station: 'Station Nord', path: 'FUI', pathId: 'fui', percentComplete: 78 },
      { id: '6', name: 'Emma Schmidt', station: 'Station Syd', path: 'FUI', pathId: 'fui', percentComplete: 54 },
      { id: '7', name: 'Nikolaj Møller', station: 'Station Øst', path: 'FUI', pathId: 'fui', percentComplete: 92 },
      { id: '8', name: 'Ida Rasmussen', station: 'Station Vest', path: 'Indsatsleder', pathId: 'indsatsleder', percentComplete: 67 },
      // Add more test data to ensure we always have content
      { id: '9', name: 'Thomas Nielsen', station: 'Station Nord', path: 'Holdleder', pathId: 'holdleder', percentComplete: 45 },
      { id: '10', name: 'Maria Larsen', station: 'Station Syd', path: 'Indsatsleder', pathId: 'indsatsleder', percentComplete: 98 },
      { id: '11', name: 'Christian Olsen', station: 'Station Øst', path: 'FUI', pathId: 'fui', percentComplete: 23 },
      { id: '12', name: 'Louise Pedersen', station: 'Station Vest', path: 'Holdleder', pathId: 'holdleder', percentComplete: 88 }
    ];
    
    
    // Apply filters if provided
    if (filters) {
      let filteredUsers = [...users];
      
      // Filter by educational path
      if (filters.educationalPathId && filters.educationalPathId !== 'all') {
        filteredUsers = filteredUsers.filter(user => user.pathId === filters.educationalPathId);
      }
      
      // Filter by new users
      if (filters.newUsers === true) {
        filteredUsers = filteredUsers.filter(user => user.percentComplete < 70);
      }
      
      // Filter by group (example implementation)
      if (filters.group && filters.group !== 'all') {
        filteredUsers = filteredUsers.filter(user => {
          if (filters.group === 'completed') return user.percentComplete === 100;
          if (filters.group === 'in-progress') return user.percentComplete > 0 && user.percentComplete < 100;
          if (filters.group === 'not-started') return user.percentComplete === 0;
          return true;
        });
      }
      
      // Apply sorting
      if (filters.sortBy) {
        const direction = filters.sortDirection === 'desc' ? -1 : 1;
        const field = filters.sortBy as keyof UserProgressDTO;
        
        filteredUsers.sort((a, b) => {
          if (a[field] < b[field]) return -1 * direction;
          if (a[field] > b[field]) return 1 * direction;
          return 0;
        });
      }
      
      users = filteredUsers;
    }
    
    
    // Ensure we always have some data to display
    if (users.length === 0) {
      
      // Add some default users that match any filter
      users = [
        { id: 'default-1', name: 'Test Bruger 1', station: 'Test Station', path: 'Test Path', pathId: filters?.educationalPathId || 'test', percentComplete: 50 },
        { id: 'default-2', name: 'Test Bruger 2', station: 'Test Station', path: 'Test Path', pathId: filters?.educationalPathId || 'test', percentComplete: 75 }
      ];
    }
    
    const result = {
      users,
      total: users.length,
      filteredPath: filters?.['educationalPathId'] || ''
    };
    
    
    return result;
  }

  /**
   * Load the current dashboard configuration from the API
   */
  private loadCurrentDashboard(): void {
    // Skip if we already have a dashboard or if API is not available
    if (this.currentDashboard$.value !== null || !this.apiAvailable) {
      return;
    }
    
    
    // Try to load from cache first to avoid unnecessary API calls
    const cachedDashboard = this.loadFromCache();
    if (cachedDashboard) {
      this.currentDashboard$.next(cachedDashboard);
      return;
    }
    
    // Try to load from API
    this.requestCurrentDashboardConfig().pipe(
      catchError((error: unknown) => {
        this.logDashboardCurrentFetchError(error, 1);

        if (error instanceof HttpErrorResponse) {
          this.handleApiError(error, 'load current dashboard');
        }

        const cachedDashboard = this.loadFromCache();
        if (cachedDashboard) {
          return of(cachedDashboard);
        }

        return of(null);
      })
    ).subscribe(dashboard => {
      if (dashboard) {
        
        // Log detailed widget information
        if (dashboard.widgets) {
        }
        
        this.currentDashboard$.next(dashboard);
        
        // Save to cache for offline use
        this.saveToCache(dashboard);
      } else {
        // API returned null dashboard - set to null
        this.currentDashboard$.next(null);
      }
    });
  }

  /**
   * Load dashboards from API
   */
  private loadAvailableDashboards(): void {
    this.http.get<DashboardSelectorDTO>(`${this.apiUrl}/list`).pipe(
      catchError(error => {
        console.error('Error loading available dashboards:', error);
        return of(null);
      })
    ).subscribe(data => {
      if (data) {
        this.availableDashboards$.next(data);
      }
    });
  }

  /**
   * Load legacy dashboard tiles
   * @deprecated Use loadCurrentDashboard instead
   */
  private loadLegacyDashboard(): void {
    const savedTiles = localStorage.getItem(DASHBOARD_STORAGE_KEY);
    if (savedTiles) {
      try {
        const tiles = JSON.parse(savedTiles) as DashboardTile[];
        this.dashboardTilesSubject.next(tiles.map(tile => new DashboardTileModel(tile)));
      } catch (error) {
        console.error('Error loading legacy dashboard:', error);
        this.dashboardTilesSubject.next([]);
      }
    } else {
      this.dashboardTilesSubject.next([]);
    }
  }

  /**
   * Load the DUNA dashboard model
   * @deprecated Use loadCurrentDashboard instead
   */
  private loadDunaDashboard(): void {
    const savedDashboard = localStorage.getItem(DUNA_DASHBOARD_STORAGE_KEY);
    if (savedDashboard) {
      try {
        const dto = JSON.parse(savedDashboard) as DashboardDTO;
        const dashboard = Dashboard.fromDTO(dto);
        this.dashboardSubject.next(dashboard);
      } catch (error) {
        console.error('Error loading DUNA dashboard:', error);
        this.currentDashboard$.next(null);
      }
    } else {
      this.currentDashboard$.next(null);
    }
  }

  /**
   * Save the dashboard to localStorage
   * @deprecated Use updateDashboard instead 
   */
  private saveDashboard(dashboard: Dashboard): void {
    try {
      const dto = dashboard.toDTO();
      localStorage.setItem(DUNA_DASHBOARD_STORAGE_KEY, JSON.stringify(dto));
    } catch (error) {
      console.error('Error saving dashboard:', error);
    }
  }

  /**
   * Get cached data if valid and not expired
   */
  private getCachedData<T>(key: string): T | null {
    const entry = this.widgetDataCache.get(key);
    
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      // Expired, delete and return null
      this.widgetDataCache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  /**
   * Cache data with TTL
   */
  private cacheData<T>(key: string, data: T, ttl: number = this.DEFAULT_CACHE_TTL): void {
    this.widgetDataCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Schedule periodic cache cleanup
   */
  private scheduleCacheCleanup(): void {
    // Clean expired cache entries every minute
    setInterval(() => this.cleanupCache(), 60 * 1000);
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    
    for (const [key, entry] of this.widgetDataCache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.widgetDataCache.delete(key);
      }
    }
  }

  /**
   * Load dashboard configuration from localStorage
   * @private
   */
  widgetsToSavePayload(widgets: Array<{
    id?: string | number;
    type?: string;
    title?: string;
    dataSource?: string;
    x?: number;
    y?: number;
    cols?: number;
    rows?: number;
    position?: { x?: number; y?: number; cols?: number; rows?: number };
    defaultFilters?: Record<string, unknown>;
    settings?: { filters?: Record<string, unknown> };
  }>): unknown[] {
    return widgets.map((widget, index) => {
      const x = widget.position?.x ?? widget.x ?? 0;
      const y = widget.position?.y ?? widget.y ?? 0;
      const cols = widget.position?.cols ?? widget.cols ?? 3;
      const rows = widget.position?.rows ?? widget.rows ?? 3;

      return {
        id: widget.id ?? `${widget.type || 'widget'}_${index}`,
        type: widget.type,
        widgetType: widget.type,
        title: widget.title,
        dataSource: widget.dataSource,
        x,
        y,
        cols,
        rows,
        position: { x, y, cols, rows },
        defaultFilters: widget.defaultFilters ?? widget.settings?.filters ?? {},
      };
    });
  }

  private normalizeDashboardConfig(dashboard: DashboardConfigDTO | null): DashboardConfigDTO | null {
    if (!dashboard || !Array.isArray(dashboard.widgets)) {
      return dashboard;
    }

    return {
      ...dashboard,
      widgets: dashboard.widgets.map((widget) => ({
        ...widget,
        defaultFilters: widget.defaultFilters || widget.filters || {},
      })),
    };
  }

  private loadFromCache(): DashboardConfigDTO | null {
    const cached = localStorage.getItem(DASHBOARD_CACHE_STORAGE_KEY);
    if (!cached) {
      return null;
    }
    
    try {
      const dashboard = JSON.parse(cached) as DashboardConfigDTO;

      // Ensure cache has meaningful data before using it
      if (!dashboard || !Array.isArray(dashboard.widgets) || dashboard.widgets.length === 0) {
        console.warn('[DashboardService] Cached dashboard is empty or invalid, clearing cache and skipping load');
        localStorage.removeItem(DASHBOARD_CACHE_STORAGE_KEY);
        return null;
      }

      // Add size validation
      if (dashboard?.widgets) {
        dashboard.widgets.forEach(widget => {
          // Ensure userProgress widgets have proper size
          if (widget.widgetType === 'userProgress' && (!widget.position || !widget.position.cols)) {
            widget.position = { 
              x: widget.position?.x || 0, 
              y: widget.position?.y || 0, 
              cols: 6, 
              rows: 4 
            };
          }
          
          // Log the size from cache
        });
      }
      
      return dashboard;
    } catch (e) {
      console.error('Failed to parse cached dashboard:', e);
      return null;
    }
  }
  
  /**
   * Save dashboard to cache
   */
  private saveToCache(dashboard: DashboardConfigDTO): void {
    try {
      localStorage.setItem(DASHBOARD_CACHE_STORAGE_KEY, JSON.stringify(dashboard));
    } catch (error) {
      console.error('Error saving dashboard to cache:', error);
    }
  }
  
  /**
   * Create a default dashboard configuration with predefined widgets
   */
  public createDefaultDashboardConfig(): DashboardConfigDTO {
    
    // Generate unique IDs for widgets
    const userProgressWidgetId = this.generateId();
    const locationProgressWidgetId = this.generateId();
    const simpleNumber1WidgetId = this.generateId();
    const simpleNumber2WidgetId = this.generateId();
    const simpleNumber3WidgetId = this.generateId();
    const userStatisticsWidgetId = this.generateId();
    
    // Create default dashboard with the new layout
    const defaultDashboard: DashboardConfigDTO = {
      id: 'default-dashboard',
      name: 'Standard Dashboard',
      description: 'Standard dashboard layout med bruger fremgang, lokation fremgang, nøgletal og bruger statistik',
      isDefault: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      widgets: [
        // 1. "Bruger fremgang" widget: columns 1-6, rows 1-3 (3 tall instead of 4)
        {
          id: userProgressWidgetId,
          widgetType: 'userProgress',
          title: 'Bruger fremgang',
          position: { x: 0, y: 0, cols: 6, rows: 3 },
          dataSource: 'userProgress',
          defaultFilters: { 
            group: 'all',
            sortBy: 'registeredPercentage',
            sortDirection: 'desc' 
          }
        },
        // 2. "Lokation Fremgang" widget: columns 1-6, rows 4-6 (underneath the user progress)
        {
          id: locationProgressWidgetId,
          widgetType: 'locationProgress',
          title: 'Lokation Fremgang',
          position: { x: 0, y: 3, cols: 6, rows: 3 },
          dataSource: 'locationProgress',
          defaultFilters: { 
            sortBy: 'registeredPercentage',
            sortDir: 'desc' 
          }
        },
        // 3. Three "Simple number" widgets on row 1:
        // Widget 1: columns 7-8
        {
          id: simpleNumber1WidgetId,
          widgetType: 'simpleNumber',
          title: 'Registreret/Fremgang Ratio',
          position: { x: 6, y: 0, cols: 2, rows: 1 },
          dataSource: 'registered_to_progress_ratio',
          defaultFilters: { 
            metric: 'registered_to_progress_ratio' 
          }
        },
        // Widget 2: columns 9-10
        {
          id: simpleNumber2WidgetId,
          widgetType: 'simpleNumber',
          title: 'Brugere med Advarsler',
          position: { x: 8, y: 0, cols: 2, rows: 1 },
          dataSource: 'users_with_warnings',
          defaultFilters: { 
            metric: 'users_with_warnings' 
          }
        },
        // Widget 3: columns 11-12
        {
          id: simpleNumber3WidgetId,
          widgetType: 'simpleNumber',
          title: 'Færdige Brugere',
          position: { x: 10, y: 0, cols: 2, rows: 1 },
          dataSource: 'completed_users',
          defaultFilters: { 
            metric: 'completed_users' 
          }
        },
        // 4. "Bruger Statistik" widget: columns 7-12, row 2 (underneath the simple number widgets)
        {
          id: userStatisticsWidgetId,
          widgetType: 'userProgressStats',
          title: 'Bruger Statistik',
          position: { x: 6, y: 1, cols: 6, rows: 4 },
          dataSource: 'userProgressStats',
          defaultFilters: { 
            showCharts: true,
            period: 'monthly' 
          }
        }
      ]
    };
    
    // Log the created dashboard for debugging
    
    // Save to cache
    this.saveToCache(defaultDashboard);
    
    return defaultDashboard;
  }

  /**
   * Generate a random ID for new widgets
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  /**
   * Utility method to log dashboard widget sizes
   */
  private logDashboardWidgetSizes(dashboard: DashboardConfigDTO): any {
    return {
      id: dashboard.id,
      name: dashboard.name,
      widgets: dashboard.widgets.map(widget => ({
        id: widget.id,
        widgetType: widget.widgetType,
        position: widget.position
      }))
    };
  }

  /**
   * Reset service state. Should be called on logout.
   */
  public reset(): void {
    
    // Reset API availability flag
    this.apiAvailable = true;
    
    // Reset dashboard and clear subjects
    this.currentDashboard$.next(null);
    this.availableDashboards$.next(null);
    this.dashboardTilesSubject.next([]);
    this.dashboardSubject.next(null);
    
    // Clear widget data cache
    this.clearWidgetDataCache();
    
    // Reset static initialization flag to allow proper re-initialization after logout
    DashboardService.hasInitialized = false;
    
    // Re-initialize the service (only if needed)
    if (!this.initializing) {
      this.initializeServices();
    }
    
  }
  
  /**
   * Force a refresh of widget data from the backend
   * @param widgetType Optional widget type to refresh
   */
  public refreshWidgetData(widgetType?: string): void {
    
    // Clear cache for specified widget type or all widgets
    this.clearWidgetDataCache(widgetType);
    
    // Reset API availability to force a fresh attempt
    this.apiAvailable = true;
    
    // If we have a current dashboard, trigger update to refresh widgets
    const currentDashboard = this.currentDashboard$.value;
    if (currentDashboard) {
      this.currentDashboard$.next({...currentDashboard});
    }
  }

  /**
   * Test API connection to see if the dashboard API is available
   * Returns an observable that resolves to true if the API is available
   */
  public testApiConnection(): Observable<boolean> {
    // Prevent multiple simultaneous API tests
    if (this.apiTestInProgress) {
      return of(this.apiAvailable);
    }

    const currentEndpoint = `${this.apiUrl}/current`;
    
    // Set test in progress flag
    this.apiTestInProgress = true;
    
    // Use HttpClient with responseType: 'text' to get raw response
    return this.http.get(currentEndpoint, { responseType: 'text', observe: 'response' }).pipe(
      map(response => {
        
        // Try to parse the JSON to verify it's valid
        try {
          const parsedData = JSON.parse(response.body || '{}');
          this.apiAvailable = true;
          return true;
        } catch (e) {
          console.error('[DASHBOARD] Response not valid JSON:', e);
          return false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('[DASHBOARD] API connection test failed:', error.status, error.message);
        
        // Try alternative endpoint with full path from environment
        const altEndpoint = `${environment.apiUrl}/current`;
        
        return this.http.get(altEndpoint, { responseType: 'text', observe: 'response' }).pipe(
          map(response => {
            
            // Try to parse the JSON
            try {
              const parsedData = JSON.parse(response.body || '{}');
              this.apiAvailable = true;
              return true;
            } catch (e) {
              console.error('[DASHBOARD] Alternative response not valid JSON:', e);
              return false;
            }
          }),
          catchError((altError: HttpErrorResponse) => {
            console.error('[DASHBOARD] Alternative API connection test failed:', altError.status, altError.message);
            this.apiAvailable = false;
            return of(false);
          })
        );
      }),
      finalize(() => {
        // Always clear the test in progress flag when done
        this.apiTestInProgress = false;
      })
    );
  }
  
  /**
   * Debug user progress data by testing all possible data retrieval methods
   * This helps identify which approach correctly fetches data
   */
  debugUserProgressData(): void {
    
    // Test approach 1: Direct API call
    const apiUrl = this.apiUrl || `${environment.apiUrl}`;
    
    this.http.post(`${apiUrl}/widget-data/userProgress`, { filters: { group: 'all' } })
      .subscribe({
        error: (err) => console.error('[DASHBOARD-DEBUG] Direct API call error:', err)
      });
    
    // Test approach 2: Using the dedicated data source function
    getUserProgressData(this.http, createListRequest({
      page: 1,
      pageSize: 50,
      filters: { group: 'all' },
      includeFilters: true
    }))
      .subscribe({
        error: (err) => console.error('[DASHBOARD-DEBUG] Data source function error:', err)
      });
    
    // Test approach 3: Using the service's getUserProgressData method
    this.getUserProgressData({ group: 'all' })
      .subscribe({
        error: (err) => console.error('[DASHBOARD-DEBUG] Service method error:', err)
      });
    
    // Test approach 4: Using the getWidgetData method
    this.getWidgetData('userProgress', 'default', { group: 'all' })
      .subscribe({
        error: (err) => console.error('[DASHBOARD-DEBUG] getWidgetData method error:', err)
      });
      
    // Check if the API URL is correctly set
    
    // Check if mock data is being generated
  }

  /**
   * Get widget permissions for the current organization
   * @returns Observable of widget permissions
   */
  getWidgetPermissions(): Observable<any[]> {
    if (!this.apiAvailable) {
      console.warn('[DashboardService] API not available, cannot get widget permissions');
      return throwError(() => new Error('API not available'));
    }

    return this.http.get<any[]>(`${this.apiUrl}/widget-permissions`)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'loading widget permissions');
          return throwError(() => error);
        })
      );
  }

  /**
   * Set widget permissions for the current organization
   * @param permissions Array of widget permissions
   * @returns Observable of the result
   */
  setWidgetPermissions(permissions: any[]): Observable<any> {
    if (!this.apiAvailable) {
      console.warn('[DashboardService] API not available, cannot set widget permissions');
      return throwError(() => new Error('API not available'));
    }

    return this.http.post<any>(`${this.apiUrl}/widget-permissions`, permissions)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'updating widget permissions');
          return throwError(() => error);
        })
      );
  }

  /**
   * Get allowed widgets for the current user role
   * @returns Observable of allowed widget types
   */
  getAllowedWidgets(): Observable<string[]> {
    if (!this.apiAvailable) {
      console.warn('[DashboardService] API not available, cannot get allowed widgets');
      return throwError(() => new Error('API not available'));
    }

    return this.http.get<string[]>(`${this.apiUrl}/allowed-widgets`)
      .pipe(
        catchError(error => {
          this.handleApiError(error, 'loading allowed widgets');
          return throwError(() => error);
        })
      );
  }

  getOrgDashboardTemplates(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/org-templates`).pipe(
      catchError((error) => {
        this.handleApiError(error, 'loading org dashboard templates');
        return of([]);
      })
    );
  }

  getTemplateAssignments(templateId: string): Observable<{ users: { userId: number; name: string; email: string }[] }> {
    return this.http.get<{ users: { userId: number; name: string; email: string }[] }>(
      `${this.apiUrl}/templates/${templateId}/assignments`
    );
  }

  getAssignmentOverview(): Observable<DashboardAssignmentOverview> {
    return this.http.get<DashboardAssignmentOverview>(`${this.apiUrl}/assignment-overview`);
  }

  putTemplateAssignments(templateId: string, userIds: number[]): Observable<unknown> {
    return this.http.put(`${this.apiUrl}/templates/${templateId}/assignments`, { userIds });
  }

  getDashboardShareLinks(): Observable<{ items: DashboardShareLinkListItem[] }> {
    return this.http.get<{ items: DashboardShareLinkListItem[] }>(`${this.apiUrl}/share-links`);
  }

  createDashboardShareLink(dashboardId: number, label?: string): Observable<{ linkUuid: string; label: string; dashboardId: number }> {
    return this.http.post<{ linkUuid: string; label: string; dashboardId: number }>(
      `${this.apiUrl}/share-links`,
      { dashboardId, label: label ?? '' }
    );
  }

  updateDashboardShareLink(
    linkUuid: string,
    payload: { label?: string; templateData?: { widgets: unknown[] }; widgets?: unknown[] }
  ): Observable<unknown> {
    return this.http.put(`${this.apiUrl}/share-links/${encodeURIComponent(linkUuid)}`, payload);
  }

  deleteDashboardShareLink(linkUuid: string): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/share-links/${encodeURIComponent(linkUuid)}`);
  }

  getPublicShareLinkDashboard(linkUuid: string): Observable<DashboardConfigDTO> {
    return this.http
      .get<DashboardConfigDTO>(`${this.apiUrl}/share-links/${encodeURIComponent(linkUuid)}`)
      .pipe(map((response) => this.normalizeDashboardConfig(response) as DashboardConfigDTO));
  }
}

export interface DashboardAssignmentOverview {
  roleFallbacks: Array<{
    role: string;
    dunaTemplateName: string | null;
    orgTemplateName: string | null;
    effectiveTemplateName: string | null;
    orgOverridesDuna: boolean;
  }>;
  userAssignments: Array<{
    userId: number;
    name: string;
    email: string | null;
    userRole: string;
    assignedTemplateName: string;
    dunaRoleFallbackName: string | null;
    orgRoleFallbackName: string | null;
    effectiveRoleFallbackName: string | null;
    differsFromDunaRoleFallback: boolean;
    differsFromEffectiveRoleFallback: boolean;
  }>;
}

export interface DashboardShareLinkListItem {
  linkUuid: string;
  label: string;
  dashboardId: number;
  dashboardName: string;
  createdAt: string;
  updatedAt: string;
}
